import { DashboardChecksTypeEnum, FileTypeEnum, accountApis, apiConfigs, fileApis, uploadApis } from "@payping/api";
import type { UserDocumentModel } from "@payping/api";
import { Add, Close, RedoOutlined, Upload as UploadIcon } from "@payping/icons";
import { getPersonalInfo, getUserChecks, getUserPhoneNumber, updatePersonalInfo } from "@payping/redux/actions";
import { SDKDateObject, dateFormats, isBeforeToday, toJalali, toUtc, utcNow } from "@payping/utils/dateUtils";
import { getFileName, getFileType } from "@payping/utils/string";
import { ValidEmail, ValidMobileNumber, ValidNationalId, ValidPersianName } from "@payping/utils/validation";
import {
  Button,
  Col,
  Divider,
  Drawer,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Modal,
  Page,
  Row,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";
import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAsyncRetry, useDeepCompareEffect } from "react-use";
import { FormLayout } from "@/components/DashUser/FormLayout";
import { SubLayout } from "@/components/SubLayout";
import { useAppDispatch, useAppSelector, useLastUrlWasPage } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DashboardRoutes } from "@/router/constant";

const PersonalEdit: React.FC = () => {
  const currentURL = useLocation().pathname;
  const isRegisterURL = currentURL === DashboardRoutes.register.personal.url;
  const [showCodeForm, setShowCodeForm] = useState<boolean>(false);
  const [removeProfileCalled, setRemoveProfileCalled] = useState<boolean>(false);
  const [removeDocCalled, setRemoveDocCalled] = useState<boolean>(false);
  const [defaultBirthPickerValue, setDefaultBirthPickerValue] = useState<SDKDateObject>();
  const dispatch = useAppDispatch();
  const [form] = LegacyForm.useForm();
  const { t } = useAppTranslation();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const profilePicture = useAppSelector((state) => state.user.personalInfo?.profilePicture);
  const [fileList, setFileList] = useState<UploadFile[]>(() => {
    if (profilePicture) {
      return [
        {
          name: "image",
          size: 0,
          type: "image/*",
          uid: "0",
          url: profilePicture.replace("payping.ir", apiConfigs.domain),
        },
      ];
    }
    return [];
  });
  const personalDocuments = useAppSelector((state) => state.user.personalInfo?.documents);
  const [nationalFilesList, setNationalFilesList] = useState<UploadFile[]>(() => {
    if (personalDocuments) {
      return personalDocuments.map(({ serverName }: UserDocumentModel, index: number) => {
        const name = serverName || "file";
        return {
          name: `${getFileName(name)} ${index + 1}`,
          size: 0,
          type: getFileType(name),
          uid: String(index),
          url: name.replace("payping.ir", apiConfigs.domain),
        };
      });
    }
    return [];
  });
  const isOfficialSubmitted = useAppSelector((state) => state.user.businessInfo?.isOfficialSubmited);
  const checks = useAppSelector((state) => state.user.checks);
  // const personalSectionStatus = Math.min(Number(checks?.personalStatus), Number(checks?.birthCertificateStatus))df;
  const isPersonalDisabled = checks?.personalStatus === DashboardChecksTypeEnum.Confirmed;
  // TODO: this should merge with isPersonalDisabled (back-end should do it)
  const isBirthCertDisabled = checks?.birthCertificateStatus === DashboardChecksTypeEnum.Confirmed;
  const navigate = useNavigate();
  const { navigateToLast } = useLastUrlWasPage();

  useMessagedAsync(() => dispatch(getUserChecks()), [dispatch], { useProgress: true });

  const [, removeDocument] = useMessagedAsyncFn(
    (id) => fileApis.removeDocumentWithIdWithCancellationTokenDelete(id),
    [],
    undefined,
    {
      useProgress: true,
    },
  );

  const { loading } = useAsyncRetry(async () => {
    await dispatch(getPersonalInfo());
    await dispatch(getUserPhoneNumber());
  }, [dispatch]);

  const utcDate =
    personalInfo?.birthDate && personalInfo?.birthDate !== "0001-01-01T00:00:00"
      ? (toJalali({
          convertTime: false,
          date: personalInfo?.birthDate,
          dateFormat: dateFormats.standard.utcDateTime,
          toFormat: dateFormats.standard.utcDate,
        }).object as SDKDateObject)
      : undefined;

  useDeepCompareEffect(() => {
    const ProfileInitialValues = {
      avatar: profilePicture,
      birthCertificateNumber: personalInfo?.birthCertificateNumber,
      birthDate: utcDate,
      email: personalInfo?.email,
      fatherName: personalInfo?.fatherName,
      firstName: personalInfo?.firstName,
      lastName: personalInfo?.lastName,
      nationalCode: personalInfo?.nationalCode,
      phoneNumber: personalInfo?.phoneNumber,
    };
    setDefaultBirthPickerValue(utcDate || utcNow({ toFormat: "YYYY" }).object.subtract(18, "years"));
    form.setFieldsValue(ProfileInitialValues);
  }, [profilePicture, personalInfo, utcDate]);

  const [{ loading: verifyPhoneNumberLoading, error: errorVerifyPhone, value: successVerifyPhone }, verifyPhoneNumber] =
    useMessagedAsyncFn(
      async () => {
        await form.validateFields();
        const response = await accountApis.verifyPhoneNumberWithRequestModelPost({
          code: form.getFieldValue("code"),
          phoneNumber: form.getFieldValue("phoneNumber"),
        });
        setShowCodeForm(false);
        return response;
      },
      [form],
      undefined,
      { loading: false, success: t("profile.personal.successVerifyPhone") },
    );

  const setTimer = useCallback(() => {
    let oneMin = 59;
    form.setFieldsValue({ timer: oneMin });
    const interval = setInterval(() => {
      oneMin -= 1;
      if (oneMin < 1) clearInterval(interval);
      form.setFieldsValue({ timer: oneMin.toString() });
    }, 1000);
  }, [form]);

  const [{ loading: getCodeLoading }, onCLickGetCode] = useMessagedAsyncFn(
    async () => {
      await form.validateFields(["phoneNumber"]);
      await accountApis.phoneNumberWithRequestModelPost({
        phoneNumber: form.getFieldValue("phoneNumber"),
      });
      setShowCodeForm(true);
      setTimer();
    },
    [form, setTimer],
    undefined,
    { success: t("profile.personal.successSendCode") },
  );

  const [{ loading: sendingEmailVerify }, sendEmailVerify] = useMessagedAsyncFn(
    async () => {
      await form.validateFields(["email"]);
      await accountApis.sendVerificationEmailGet(/* form.getFieldValue("email") */);
    },
    [form],
    undefined,
    { success: t("profile.personal.successSendEmail") },
  );

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: any) => {
      const { avatar, documents, ...data } = values;
      const profilePictureFileName =
        typeof avatar === "string" ? avatar.split("/")[avatar.split("/").length - 1] : avatar?.fileList[0]?.response;
      const birthDate = toUtc({
        convertTime: false,
        date: data?.birthDate,
        toFormat: dateFormats.standard.utcDate,
      }).string;
      const finalPersonalInfo = {
        ...data,
        avatar: profilePictureFileName,
        birthDate,
      };
      await dispatch(updatePersonalInfo(finalPersonalInfo));
      // TODO: the method for uploading user profile picture SHOULD change in backend (upload project)
      if (removeProfileCalled) {
        await fileApis.removeProfilePictureWithCancellationTokenDelete();
      }
      if (profilePictureFileName) {
        await fileApis.addFileWithRequestModelWithCancellationTokenPost({
          fileName: profilePictureFileName,
          type: FileTypeEnum.ProfilePicture,
        });
      }
      if (removeDocCalled && personalDocuments) {
        await Promise.all(
          personalDocuments.map(async (doc: UserDocumentModel) => {
            await removeDocument(doc.id);
          }),
        );
      }
      if (documents) {
        await Promise.all(
          documents.fileList.map(async (doc: UploadFile) => {
            const fileName = doc.url ? doc.url.split("/").pop() : doc.response;
            if (fileName)
              await fileApis.addFileWithRequestModelWithCancellationTokenPost({
                fileName,
                type: FileTypeEnum.ShensanameKartMelli,
              });
          }),
        );
      }
      if (isRegisterURL) navigate(DashboardRoutes.register.business.url);
      if (!isRegisterURL) navigate(DashboardRoutes.settings.personal.root.url);
    },
    [dispatch, removeProfileCalled, removeDocCalled, personalDocuments, isRegisterURL, navigate, removeDocument],
    undefined,
    { useProgress: true },
  );

  return (
    <Drawer
      title={isRegisterURL ? t("settings.business.paymentGatewayRequestForm") : t("settings.business.editForm")}
      open={true}
      size={"100%"}
      placement={"bottom"}
      closeIcon={false}
      extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={() => navigateToLast()} />}
    >
      <Page>
        <SubLayout>
          <FormLayout
            title={
              <Typography.Title className={"tw:m-0! tw:mb-4!"} level={4}>
                {t("profile.main.personal")}
              </Typography.Title>
            }
            instructions={
              <>
                <p>{t("settings.personal.instructions.firstNameAndLastName")}</p>
                <p>{t("settings.personal.instructions.nationalIdAndBirthdate")}</p>
                <p>{t("settings.personal.instructions.nationalIdAndBirthdatePhoneNumber")}</p>
                {isOfficialSubmitted ? <p>{t("settings.personal.instructions.legalAttention")}</p> : null}
              </>
            }
          >
            <LegacyForm form={form} onFinish={onSubmit} scrollToFirstError={{ behavior: "smooth" }} layout={"vertical"}>
              <LegacyForm.Item name="avatar">
                <Upload
                  disabled={loading}
                  listType={"picture-circle"}
                  requestFunction={uploadApis.profilePicWithFilePost}
                  onChange={(info: UploadChangeParam): void => setFileList(info.fileList)}
                  fileList={fileList}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                  onRemove={() => setRemoveProfileCalled(true)}
                  allowedExtensions={["jpg", "jpeg", "png"]}
                  showTooltip
                >
                  {!fileList.length && (
                    <div>
                      <Add width={"20px"} />
                      <div className={"ant-upload-text"}>{t("profile.avatar")}</div>
                    </div>
                  )}
                </Upload>
              </LegacyForm.Item>
              <LegacyForm.Item
                label={t("firstName")}
                name="firstName"
                rules={[
                  () => ({
                    validator(_, value): Promise<void> {
                      if (ValidPersianName(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("profile.validFirstName")));
                    },
                  }),
                  { required: true },
                ]}
              >
                <Input disabled={loading || (!form.getFieldValue("firstName") ? false : isPersonalDisabled)} />
              </LegacyForm.Item>
              <LegacyForm.Item
                label={t("lastName")}
                name="lastName"
                rules={[
                  {
                    validator(_, value): Promise<void> {
                      if (ValidPersianName(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("profile.validLastName")));
                    },
                  },
                  { required: true },
                ]}
              >
                <Input disabled={loading || (!form.getFieldValue("lastName") ? false : isPersonalDisabled)} />
              </LegacyForm.Item>

              <Row gutter={[8, 0]}>
                <Col span={12}>
                  <LegacyForm.Item
                    label={t("nationalCode")}
                    name="nationalCode"
                    rules={[
                      () => ({
                        validator(_, value): Promise<void> {
                          if (ValidNationalId(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("profile.validBNationalCode")));
                        },
                      }),
                      { message: t("error.requiredForTaxCode", { name: t("nationalCode") }), required: true },
                    ]}
                  >
                    <Input disabled={loading || (!form.getFieldValue("nationalCode") ? false : isPersonalDisabled)} />
                  </LegacyForm.Item>
                </Col>
                <Col span={12}>
                  <LegacyForm.Item
                    label={t("birthDate")}
                    name="birthDate"
                    rules={[
                      { required: true },
                      {
                        validator(_, value) {
                          if (value && !isBeforeToday(value)) {
                            return Promise.reject(new Error(t("profile.birthDateValidation")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <LegacyDatePicker
                      className={"tw:w-full"}
                      skeleton={loading}
                      disabled={!form.getFieldValue("birthDate") ? false : isPersonalDisabled}
                      disabledDate={(date) => !isBeforeToday(date)}
                      defaultPickerValue={defaultBirthPickerValue}
                      dateOrder={["YearPicker", "MonthPicker", "DatePicker"]}
                    />
                  </LegacyForm.Item>
                </Col>
                <Col span={12}>
                  <LegacyForm.Item
                    label={t("mobile")}
                    shouldUpdate={(prev, next) => prev.phoneNumber !== next.phoneNumber || prev.timer !== next.timer}
                    required
                  >
                    {(phoneNumberInputField) => (
                      <Row gutter={[8, 8]}>
                        <Col xs={24} lg={24}>
                          <LegacyForm.Item
                            noStyle
                            rules={[
                              () => ({
                                validator(_, value) {
                                  if (ValidMobileNumber(value)) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error(t("profile.validPhoneNumber")));
                                },
                              }),
                              { required: true },
                            ]}
                            name={"phoneNumber"}
                          >
                            <Input
                              disabled={
                                loading ||
                                isPersonalDisabled ||
                                phoneNumberInputField.getFieldValue("timer") > 0 ||
                                showCodeForm
                              }
                              maxLength={11}
                            />
                          </LegacyForm.Item>
                        </Col>
                        {successVerifyPhone?.verificationSucceeded && (
                          <Col xs={24} lg={24}>
                            <Typography.Text type={"success"}>{successVerifyPhone.message}</Typography.Text>
                          </Col>
                        )}
                        {(!personalInfo?.phoneNumberConfirmed ||
                          phoneNumberInputField.getFieldValue("phoneNumber") !== personalInfo?.phoneNumber) &&
                          !successVerifyPhone?.verificationSucceeded && (
                            <Col xs={24} lg={10}>
                              <LegacyForm.Item noStyle>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  loading={loading || getCodeLoading}
                                  disabled={
                                    form.getFieldValue("timer") > 0 ||
                                    showCodeForm ||
                                    form.isFieldsTouched(true) ||
                                    phoneNumberInputField.getFieldValue("phoneNumber")?.length < 11 ||
                                    isPersonalDisabled
                                  }
                                  onClick={onCLickGetCode}
                                >
                                  {phoneNumberInputField.getFieldValue("timer") > 0 ? (
                                    <Flex gap={"small"}>
                                      <Typography.Text>{t("nextRequestUntil")}</Typography.Text>
                                      <Typography.Text>{phoneNumberInputField.getFieldValue("timer")}</Typography.Text>
                                      <Typography.Text>{t("anotherSec")}</Typography.Text>
                                    </Flex>
                                  ) : (
                                    t("profile.verifyPhoneNumber")
                                  )}
                                </Button>
                              </LegacyForm.Item>
                            </Col>
                          )}
                      </Row>
                    )}
                  </LegacyForm.Item>
                </Col>
                <Col span={12}>
                  <LegacyForm.Item
                    label={t("profile.birthCertificateNumber")}
                    name="birthCertificateNumber"
                    rules={[{ required: true }]}
                  >
                    <Input
                      disabled={
                        loading || (!form.getFieldValue("birthCertificateNumber") ? false : isBirthCertDisabled)
                      }
                    />
                  </LegacyForm.Item>
                </Col>
                <Col span={12}>
                  <LegacyForm.Item label={t("profile.fatherName")} name="fatherName" rules={[{ required: true }]}>
                    <Input disabled={loading || (!form.getFieldValue("fatherName") ? false : isBirthCertDisabled)} />
                  </LegacyForm.Item>
                </Col>
              </Row>

              <LegacyForm.Item label={t("email")} shouldUpdate={(prev, next) => prev.email !== next.email} required>
                {(tmpForm) => (
                  <Row gutter={[8, 8]}>
                    <Col xs={24} lg={14}>
                      <LegacyForm.Item
                        name="email"
                        rules={[
                          () => ({
                            validator(_, value) {
                              if (ValidEmail(value)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(t("profile.validEmail")));
                            },
                          }),
                          { required: true },
                        ]}
                        noStyle
                        normalize={(value) => value.trim()}
                      >
                        <Input disabled={loading} />
                      </LegacyForm.Item>
                    </Col>
                    {(!personalInfo?.emailVerified || tmpForm.getFieldValue("email") !== personalInfo?.email) && (
                      <Col xs={24} lg={10}>
                        <LegacyForm.Item noStyle>
                          <Button
                            variant="outlined"
                            color="primary"
                            loading={loading || sendingEmailVerify}
                            disabled={form.isFieldsTouched(true)}
                            onClick={sendEmailVerify}
                          >
                            {t("profile.verifyEmail")}
                          </Button>
                        </LegacyForm.Item>
                      </Col>
                    )}
                  </Row>
                )}
              </LegacyForm.Item>
              <LegacyForm.Item
                noStyle
                shouldUpdate={(prevValues, currentValues): boolean => prevValues.code !== currentValues.code}
              >
                {(modalForm) => (
                  <Modal
                    title={t("profile.verifyPhoneNumber")}
                    open={showCodeForm}
                    okButtonProps={{
                      disabled: !modalForm.getFieldValue("code") || modalForm.getFieldValue("code").length < 6,
                      loading: loading || verifyPhoneNumberLoading,
                    }}
                    onOk={async () => {
                      await verifyPhoneNumber();
                      form.resetFields(["code"]);
                    }}
                    onCancel={() => {
                      form.resetFields(["code"]);
                      setShowCodeForm(false);
                    }}
                    destroyOnHidden
                    mask={{ closable: false }}
                  >
                    <Divider className={"color--gray300 tw:mt-0!"} />
                    <Row gutter={[8, 16]} className={"tw:mt-4!"}>
                      <Col span={24}>
                        <Typography.Text className="tw:font-medium">{t("profile.enterCode")}</Typography.Text>
                      </Col>
                      <Col xs={24} lg={16}>
                        <LegacyForm.Item name="code" label={t("profile.sixDigitCode")} required>
                          <Input
                            maxLength={6}
                            placeholder={t("enterCode")}
                            disabled={loading || verifyPhoneNumberLoading}
                          />
                        </LegacyForm.Item>

                        {errorVerifyPhone?.name === "BadRequestError" && (
                          <Typography.Text type={"danger"} className={"tw:text-center"}>
                            {t("error.wrongCode")}
                          </Typography.Text>
                        )}
                      </Col>
                      <Col xs={24} lg={8}>
                        <LegacyForm.Item
                          shouldUpdate={(prevValues, currentValues): boolean =>
                            prevValues.timer !== currentValues.timer
                          }
                        >
                          {({ getFieldValue }) => {
                            const remaining = getFieldValue("timer");
                            if (Number(remaining) > 0) {
                              return (
                                <LegacyForm.Item name={"timer"}>
                                  <Typography.Text>
                                    {remaining} {t("second")}{" "}
                                  </Typography.Text>
                                </LegacyForm.Item>
                              );
                            }
                            return (
                              <Button
                                size="small"
                                icon={<RedoOutlined width={14} height={14} />}
                                loading={getCodeLoading}
                                onClick={onCLickGetCode}
                                type="link"
                              >
                                {t("sendAgain")}
                              </Button>
                            );
                          }}
                        </LegacyForm.Item>
                      </Col>
                    </Row>
                  </Modal>
                )}
              </LegacyForm.Item>
              <LegacyForm.Item name={"documents"}>
                <Upload
                  disabled={loading}
                  crop={false}
                  listType={"picture-card"}
                  requestFunction={uploadApis.miscWithFilePost}
                  onChange={(info: UploadChangeParam): void => setNationalFilesList(info.fileList)}
                  fileList={nationalFilesList}
                  multiple={true}
                  maxCount={2}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: !isPersonalDisabled }}
                  onRemove={() => setRemoveDocCalled(true)}
                  allowedExtensions={["jpg", "jpeg", "png"]}
                >
                  {nationalFilesList.length < 2 && (
                    <div>
                      <UploadIcon width={"20px"} />
                      <div className={"ant-upload-text"}>{t("profile.uploadNationalFiles")}</div>
                    </div>
                  )}
                </Upload>
              </LegacyForm.Item>
              {isRegisterURL ? (
                <Row gutter={[8, 0]} className={"tw:w-full"}>
                  <Col span={11}>
                    <Button
                      htmlType={"submit"}
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      type={"primary"}
                    >
                      {t("continue")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[8, 0]}>
                  <Col span={11}>
                    <Button
                      className={"tw:w-full"}
                      type={"primary"}
                      loading={loading || loadingSubmit}
                      htmlType={"submit"}
                    >
                      {t("save")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Col>
                </Row>
              )}
            </LegacyForm>
          </FormLayout>
        </SubLayout>
      </Page>
    </Drawer>
  );
};

export default PersonalEdit;
